'use client';

import type { ListDetail } from '@swishapp/api-client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from '../../../responsive-dialog';
import { Button } from '../../../ui/button';
import { DialogFooter } from '../../../ui/dialog';

interface DeleteBoardDialogProps {
  board: ListDetail;
  onDelete: () => void;
  isDeleting?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
}

export function DeleteBoardDialog({
  board,
  onDelete,
  isDeleting = false,
  open: controlledOpen,
  onOpenChange,
  children,
  primaryButtonClassName,
  secondaryButtonClassName,
}: DeleteBoardDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const handleDelete = () => {
    onDelete();
    setOpen(false);
  };

  return (
    <ResponsiveDialog isOpen={open} onOpenChange={setOpen}>
      {children && (
        <ResponsiveDialogTrigger asChild>{children}</ResponsiveDialogTrigger>
      )}
      <ResponsiveDialogContent
        className="max-w-md"
        title="Delete Board"
        description={`Are you sure you want to delete "${board.name}"? This action cannot be undone.`}
      >
        <div className="pt-2">
          <DialogFooter>
            <ResponsiveDialogClose asChild>
              <Button
                className={secondaryButtonClassName}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              className={primaryButtonClassName}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Board'}
            </Button>
          </DialogFooter>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
